import React, { useEffect, useState } from 'react';
import has from 'lodash/has';
import { useSelector } from 'react-redux';
import { Alert, AlertType } from '@yme/atoms/alert';
import { Dropdown } from '@yme/atoms/dropdown';
import { Col, Container, Row } from '@yme/atoms/grid';
import { Pagination } from '@yme/atoms/pagination';
import { SpinnerLoader } from '@yme/atoms/spinner-loader';
import { useWindowSize } from '@yme/common/hooks';
import { useOwnerManualsLogic } from '@yme/components/OwnerManuals/useOwnerManualsLogic';
import { IndentWrapper } from '@yme/components/Separator';
import { getRTL } from '@yme/selectors/common';
import { useTranslation } from '@yme/services/i18n';
import { colorCoreWhite1000 } from '@yme/tokens/colors';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { HeadingLevel2, RegularTextLevel3 } from '@yme/tokens/typography';
import { buildKey, buildStateParam } from '@yme/utils/common';
import { getOwnerManuals, getOwnerManualsCategories } from '../../api/getOwnerManualsData';
import { OwnerManualsItem } from './OwnerManualsItem';
import { MANUALS_WRAPPER_ID, OWNER_MANUALS_CATEGORY_FILTER_DATA_ID, OWNER_MANUALS_ERROR_ALERT_ID, OWNER_MANUALS_LANGUAGE_FILTER_DATA_ID, OWNER_MANUALS_MODEL_FILTER_DATA_ID, OWNER_MANUALS_SEGMENT_FILTER_DATA_ID, OWNER_MANUALS_YEAR_FILTER_DATA_ID, } from './constants';
import { DropdownWrapperStyled, OwnerManualsListStyled, SeparatorStyled } from './styled';
export function OwnerManuals({ labels }) {
    const [data, setData] = useState([]);
    const [categories, setCategories] = useState([]);
    const [isDataFetching, setIsDataFetching] = useState(false);
    const [isError, setIsError] = useState(false);
    const { t } = useTranslation();
    const placeholders = {
        categoryPlaceholder: t('ownerManuals_categoryFilter_placeholder'),
        segmentPlaceholder: t('ownerManuals_segmentFilter_placeholder'),
        modelPlaceholder: t('ownerManuals_modelFilter_placeholder'),
        yearPlaceholder: t('ownerManuals_yearFilter_placeholder'),
        languagePlaceholder: t('ownerManuals_languageFilter_placeholder'),
    };
    const { categoryPlaceholder, segmentPlaceholder, modelPlaceholder, yearPlaceholder, languagePlaceholder, } = placeholders;
    const isRightToLeft = useSelector(getRTL);
    const { isInfiniteDesktopView, isMobileView } = useWindowSize();
    const { categoryFilterOnChange, segmentFilterOnChange, modelFilterOnChange, yearFilterOnChange, languageFilterOnChange, onFocusFilterHandler, activeItems, filtersData, manuals, 
    //pagination params
    config, paginationConfig, paginationConfigData, activeItemValue, onClickItemHandler, setCurrentPerPageValue, } = useOwnerManualsLogic({
        data,
        categories,
        placeholders,
    });
    useEffect(() => {
        setIsDataFetching(true);
        getOwnerManualsCategories()
            .then((data) => {
            setCategories(data);
        })
            .catch(() => {
            setIsError(true);
        })
            .finally(() => {
            setIsDataFetching(false);
        });
    }, []);
    useEffect(() => {
        setIsDataFetching(true);
        if (activeItems.category) {
            getOwnerManuals(activeItems.category)
                .then((response) => {
                const dataFromRequest = response;
                const isMessage = has(dataFromRequest, 'message');
                if (isMessage) {
                    setIsError(true);
                }
                else {
                    setData(dataFromRequest);
                    setIsError(false);
                }
            })
                .catch(() => {
                setIsError(true);
            })
                .finally(() => {
                setIsDataFetching(false);
            });
        }
    }, [activeItems.category]);
    const manualsList = manuals.map((manual) => (React.createElement(OwnerManualsItem, { isRightToLeft: isRightToLeft, key: buildKey(buildStateParam()), manual: manual })));
    if (!labels) {
        return null;
    }
    return (React.createElement(IndentWrapper, null,
        React.createElement(Container, { isRightToLeft: isRightToLeft, bgColor: colorCoreWhite1000, pt: "40px", pb: "56px" },
            React.createElement(Row, { mb: "16px" },
                React.createElement(Col, null,
                    React.createElement(HeadingLevel2, null, t('ownerManuals_header_label')))),
            React.createElement(Row, { mb: isInfiniteDesktopView ? '32px' : '24px' },
                React.createElement(Col, null,
                    React.createElement(RegularTextLevel3, null, t('ownerManuals_description_label')))),
            React.createElement(Row, null,
                React.createElement(Col, null,
                    React.createElement(FlexWrapper, { flexWrap: !isInfiniteDesktopView ? 'wrap' : undefined, gap: "24px" },
                        React.createElement(DropdownWrapperStyled, null,
                            React.createElement(Dropdown, { activeItem: activeItems.category || '', items: filtersData.categoryData, labelText: t('ownerManuals_categoryFilter_label'), inputPlaceholder: categoryPlaceholder, id: OWNER_MANUALS_CATEGORY_FILTER_DATA_ID, isRightToLeft: isRightToLeft, onChange: (value) => categoryFilterOnChange(value), focusHandler: onFocusFilterHandler, isDisabled: isError })),
                        React.createElement(DropdownWrapperStyled, null,
                            React.createElement(Dropdown, { activeItem: activeItems.segment || '', items: filtersData.segmentData, labelText: t('ownerManuals_segmentFilter_label'), inputPlaceholder: segmentPlaceholder, id: OWNER_MANUALS_SEGMENT_FILTER_DATA_ID, isRightToLeft: isRightToLeft, onChange: (value) => segmentFilterOnChange(value), focusHandler: onFocusFilterHandler, isDisabled: !filtersData.segmentData?.length })),
                        React.createElement(DropdownWrapperStyled, null,
                            React.createElement(Dropdown, { activeItem: activeItems.model || '', items: filtersData.modelData, labelText: t('ownerManuals_modelFilter_label'), inputPlaceholder: modelPlaceholder, id: OWNER_MANUALS_MODEL_FILTER_DATA_ID, isRightToLeft: isRightToLeft, onChange: (value) => modelFilterOnChange(value), focusHandler: onFocusFilterHandler, isDisabled: !filtersData.modelData?.length })),
                        React.createElement(DropdownWrapperStyled, null,
                            React.createElement(Dropdown, { activeItem: activeItems.year || '', items: filtersData.yearData, labelText: t('ownerManuals_yearFilter_label'), inputPlaceholder: yearPlaceholder, id: OWNER_MANUALS_YEAR_FILTER_DATA_ID, isRightToLeft: isRightToLeft, onChange: (value) => yearFilterOnChange(value), focusHandler: onFocusFilterHandler, isDisabled: !filtersData.yearData?.length })),
                        React.createElement(DropdownWrapperStyled, null,
                            React.createElement(Dropdown, { activeItem: activeItems.language || '', items: filtersData.languageData, labelText: t('ownerManuals_languageFilter_label'), inputPlaceholder: languagePlaceholder, id: OWNER_MANUALS_LANGUAGE_FILTER_DATA_ID, isRightToLeft: isRightToLeft, onChange: (value) => languageFilterOnChange(value), focusHandler: onFocusFilterHandler, isDisabled: !filtersData.languageData?.length }))))),
            Boolean(manuals.length) && (React.createElement(Row, null,
                React.createElement(Col, null,
                    React.createElement(SeparatorStyled, null)))),
            Boolean(manuals.length) && (React.createElement(React.Fragment, null,
                React.createElement(Row, { id: MANUALS_WRAPPER_ID },
                    React.createElement(Col, null,
                        React.createElement(OwnerManualsListStyled, null, manualsList))),
                React.createElement(Row, { mt: "24px" },
                    React.createElement(Col, null, Boolean(config) && (React.createElement(Pagination
                    // common props
                    , { 
                        // common props
                        isRightToLeft: isRightToLeft, isMobileView: isMobileView, 
                        // Pagination props
                        paginationData: paginationConfigData, paginationConfig: paginationConfig, activeItemValue: activeItemValue, onClickItemHandler: onClickItemHandler, 
                        // PerPage props
                        perPageLabel: t('filters_itemsPerPage_label'), currentPerPageValue: config.perPage, setCurrentPerPageValue: setCurrentPerPageValue, scrollToElement: {
                            id: MANUALS_WRAPPER_ID,
                            yOffset: 160,
                        } })))))),
            isError && (React.createElement(Row, { mt: "24px" },
                React.createElement(Col, null,
                    React.createElement(Alert, { type: AlertType.ERROR, message: t('common_somethingWentWrong_message'), dataTestId: OWNER_MANUALS_ERROR_ALERT_ID }))))),
        React.createElement(SpinnerLoader, { isLoading: isDataFetching, fullWidth: true })));
}
